/**
 * createPreferenceStore — tiny localStorage-backed preference with same-tab pub/sub.
 *
 * Several player preferences (autoplay, theater mode, playback speed) need to be
 * shared between distant components without a context provider: the value is
 * persisted in localStorage and change notifications travel through a
 * CustomEvent, so any subscriber in the tab stays in sync.
 */
export default function createPreferenceStore({ storageKey, eventName, defaultValue, parse, serialize }) {
    const read = () => {
        try {
            const raw = localStorage.getItem(storageKey);
            if (raw === null) return defaultValue;
            return parse ? parse(raw) : raw;
        } catch {
            return defaultValue;
        }
    };

    const write = (value) => {
        try {
            localStorage.setItem(storageKey, serialize ? serialize(value) : String(value));
        } catch {
            // storage unavailable (private mode) — the in-tab event still works
        }
        window.dispatchEvent(new CustomEvent(eventName, { detail: { value } }));
    };

    const subscribe = (listener) => {
        const handler = (event) => listener(event.detail?.value);
        window.addEventListener(eventName, handler);
        return () => window.removeEventListener(eventName, handler);
    };

    return { read, write, subscribe };
}
